import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Virus,
  Plus,
  MagnifyingGlass,
  DotsThree,
  Eye,
  PencilSimple,
  Trash,
  GitBranch,
  ArrowLeft,
  FloppyDisk,
  Tag,
  X,
  UploadSimple
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import ImportModal from '../../components/ImportModal';
import FileAttachments from '../../components/FileAttachments';

// Strains List Page
export const StrainsPage = () => {
  const navigate = useNavigate();
  const [strains, setStrains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    loadStrains();
  }, []);

  const loadStrains = async () => {
    try {
      const data = await api.getStrains();
      setStrains(data);
    } catch (error) {
      console.error('Failed to load strains:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this strain?')) {
      try {
        await api.deleteStrain(id);
        setStrains(strains.filter(s => s.id !== id));
        toast.success('Strain deleted');
      } catch (error) {
        toast.error('Failed to delete strain');
      }
    }
  };

  const filteredStrains = strains.filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.organism?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.genotype?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="strains-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Strains</h1>
          <p className="text-zinc-500 mt-1">Microbial strain collection</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="btn-secondary flex items-center gap-2" onClick={() => setShowImport(true)} data-testid="import-strains-btn">
            <UploadSimple size={16} /> Import
          </Button>
          <Link to="/strains/new">
            <Button className="btn-primary flex items-center gap-2" data-testid="new-strain-btn">
              <Plus size={16} /> New Strain
            </Button>
          </Link>
        </div>
      </div>

      <div className="card-flat p-4 mb-6">
        <div className="relative max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            type="text"
            placeholder="Search strains..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 input-field w-full"
            data-testid="search-strains"
          />
        </div>
      </div>

      {filteredStrains.length > 0 ? (
        <div className="card-flat overflow-hidden">
          <table className="data-table" data-testid="strains-table">
            <thead>
              <tr>
                <th className="table-header">Name</th>
                <th className="table-header">Organism</th>
                <th className="table-header">Genotype</th>
                <th className="table-header">Created</th>
                <th className="table-header w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filteredStrains.map((strain) => (
                <tr key={strain.id} className="table-row" data-testid={`strain-row-${strain.id}`}>
                  <td className="table-cell">
                    <Link to={`/strains/${strain.id}`} className="font-medium text-slate-100 hover:text-indigo-300">
                      {strain.name}
                    </Link>
                  </td>
                  <td className="table-cell">
                    <span className="text-xs bg-green-100 text-green-400 px-2 py-0.5 rounded-sm">
                      {strain.organism}
                    </span>
                  </td>
                  <td className="table-cell text-zinc-500 font-mono text-xs max-w-xs truncate">
                    {strain.genotype || '-'}
                  </td>
                  <td className="table-cell text-zinc-500">
                    {new Date(strain.created_at).toLocaleDateString()}
                  </td>
                  <td className="table-cell">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 hover:bg-zinc-100 rounded-sm"><DotsThree size={20} /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/strains/${strain.id}`)}>
                          <Eye size={16} className="mr-2" /> View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/strains/${strain.id}/lineage`)}>
                          <GitBranch size={16} className="mr-2" /> Lineage
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/strains/${strain.id}/edit`)}>
                          <PencilSimple size={16} className="mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(strain.id)} className="text-red-400">
                          <Trash size={16} className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state card-flat">
          <Virus size={48} className="text-zinc-300 mb-4" />
          <h3 className="heading-card text-slate-100 mb-2">No strains yet</h3>
          <p className="text-sm text-zinc-500 mb-4">Start building your strain collection</p>
          <Link to="/strains/new"><Button className="btn-primary">Add Strain</Button></Link>
        </div>
      )}
      {showImport && <ImportModal type="strains" onClose={() => setShowImport(false)} onImportComplete={loadStrains} />}
    </div>
  );
};

// Strain Form
export const StrainForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [strains, setStrains] = useState([]);
  const [tagInput, setTagInput] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    organism: '',
    genotype: '',
    description: '',
    parent_id: '',
    tags: []
  });

  useEffect(() => {
    loadStrains();
    if (isEdit && id) loadStrain();
  }, [id, isEdit]);

  const loadStrains = async () => {
    try {
      const data = await api.getStrains();
      setStrains(data);
    } catch (error) {
      console.error('Failed to load strains:', error);
    }
  };

  const loadStrain = async () => {
    try {
      const data = await api.getStrain(id);
      setFormData(data);
    } catch (error) {
      toast.error('Failed to load strain');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await api.updateStrain(id, formData);
        toast.success('Strain updated');
      } else {
        const result = await api.createStrain(formData);
        toast.success('Strain created');
        navigate(`/strains/${result.id}`);
      }
    } catch (error) {
      toast.error('Failed to save strain');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="strain-form-page">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/strains" className="p-2 hover:bg-zinc-100 rounded-sm"><ArrowLeft size={20} /></Link>
        <div>
          <h1 className="heading-display">{isEdit ? 'Edit Strain' : 'New Strain'}</h1>
          <p className="text-zinc-500 mt-1">{isEdit ? 'Update strain details' : 'Add a new strain'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-flat p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="e.g., DH5α" className="input-field w-full mt-1" required data-testid="strain-name-input" />
                </div>
                <div>
                  <Label htmlFor="organism">Organism *</Label>
                  <Input id="organism" name="organism" value={formData.organism} onChange={handleChange} placeholder="e.g., E. coli" className="input-field w-full mt-1" required data-testid="strain-organism-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="genotype">Genotype</Label>
                <Input id="genotype" name="genotype" value={formData.genotype} onChange={handleChange} placeholder="e.g., F- endA1 recA1 relA1 gyrA96 hsdR17" className="input-field w-full mt-1 font-mono text-sm" />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" name="description" value={formData.description} onChange={handleChange} placeholder="Additional notes..." className="input-field w-full mt-1" />
              </div>
              <div>
                <Label htmlFor="parent_id">Parent Strain (for lineage)</Label>
                <select id="parent_id" name="parent_id" value={formData.parent_id || ''} onChange={handleChange} className="input-field w-full mt-1">
                  <option value="">None (original strain)</option>
                  {strains.filter(s => s.id !== id).map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.organism})</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="card-flat p-6">
              <Label>Tags</Label>
              <div className="flex gap-2 mt-2">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())} placeholder="Add tag" className="input-field flex-1" />
                <Button type="button" variant="outline" onClick={handleAddTag}><Tag size={16} /></Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 px-2 py-1 text-sm rounded-sm">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-red-400"><X size={12} /></button>
                  </span>
                ))}
              </div>
            </div>

            <Button type="submit" className="btn-primary w-full" disabled={saving} data-testid="save-strain-btn">
              {saving ? <><div className="spinner w-4 h-4 mr-2" />Saving...</> : <><FloppyDisk size={16} className="mr-2" />{isEdit ? 'Update' : 'Create'} Strain</>}
            </Button>
          </div>
        </div>
      </form>

      {isEdit && id && (
        <div className="card-flat p-5 mt-4">
          <FileAttachments entityType="strains" entityId={id} />
        </div>
      )}
    </div>
  );
};

export default StrainsPage;
